import type { NextPage } from 'next'

import {
  SDS_SPACE_LARGE,
  SDS_SPACE_XSMALL,
} from '@s-group/design-system-tokens/web/tokens/space.es6'
import { getConfig } from '@shared/config'
import { minWidthFromTheme } from '@sok/design-system'
import { CheckoutButton } from 'components/CheckoutButton'
import { ComplementaryProductsMobile } from 'components/OrderSummary/ComplementaryProducts'
import { SummaryLayout } from 'components/OrderSummary/SummaryLayout'
import { withSSRLoading } from 'hocs/with-ssr-loading'
import { NextSeo } from 'next-seo'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ComplementaryProductsContextProvider } from 'services/ComplementaryProducts/complementary-products-provider'
import styled, { useTheme } from 'styled-components'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { trackCheckout } from 'utils/tracking/ecommerce'
import { CheckoutSteps, TrackingContext } from 'utils/tracking/interfaces/data-layer-events'
import { CartSummaryContainer } from 'views/shopping-cart/containers/CartSummaryContainer'

const { domain } = getConfig()

const SummaryPage: NextPage = () => {
  const theme = useTheme()
  const { t } = useTranslation()

  useEffect((): void => {
    trackCheckout(CheckoutSteps.REVIEW_ORDER)
  }, [])

  const { isSIdEnabled } = domain

  return (
    <>
      <NextSeo title={`${t('Shopping cart')} | ${theme.seoTags.title}`} noindex />
      <TrackingContextProvider
        trackingContext={TrackingContext.SHOPPING_CART}
        listName={'shopping-cart'}
      >
        <ComplementaryProductsContextProvider>
          <SummaryLayout data-test-id={'page__summary'}>
            <CartSummaryContainer />
            <ComplementaryProductsMobile />
            {isSIdEnabled && (
              <StickyCheckoutButton data-test-id="checkout-button-mobile" showPrice>
                {t('To checkout')}
              </StickyCheckoutButton>
            )}
          </SummaryLayout>
        </ComplementaryProductsContextProvider>
      </TrackingContextProvider>
    </>
  )
}

const StickyCheckoutButton = styled(CheckoutButton)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  bottom: 0,
  marginTop: SDS_SPACE_LARGE,
  padding: `${SDS_SPACE_XSMALL} ${SDS_SPACE_XSMALL}`,
  position: 'sticky',

  [minWidthFromTheme(theme).desktop]: {
    display: 'none',
  },
}))

export default withSSRLoading(SummaryPage)
